import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Input } from 'semantic-ui-react';

// Utility function for handling category editing
export const handleEditCategory = (category, setCategorySelected, setEditCategoryModal) => {
  setCategorySelected(category);
  setEditCategoryModal(true);
};

const EditCategoryModal = ({
  categorySelected,
  incomeAreas,
  expenseAreas,
  formRef
}) => {
  const { t } = useTranslation('finance');
  const forms = useRef({});

  // Initialize form data when component mounts or categorySelected changes
  React.useEffect(() => {
    if (formRef && categorySelected) {
      formRef.current.categoryName = categorySelected.label || '';
      formRef.current.areasToMove = [];
    }
  }, [formRef, categorySelected]);

  return (
    <div className="tw-space-y-6">
      {/* Header with category info */}
      <div className="tw-flex tw-items-center tw-gap-3 tw-pb-4 tw-border-b tw-border-gray-200">
        <div className="tw-p-2 tw-rounded-lg tw-bg-blue-100">
          <Icon name="edit" color="blue" size="large" />
        </div>
        <div>
          <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900">
            {t('categoryModals.editCategory')}
          </h3>
          <p className="tw-text-sm tw-text-gray-600">
            {t('categoryModals.editCategoryModify', { name: categorySelected?.label })}
          </p>
        </div>
      </div>

      {/* Category name input */}
      <div className="tw-space-y-2">
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
          {t('categoryModals.newCategoryName')} <span className="tw-text-red-500">*</span>
        </label>
        <Input
          fluid
          placeholder={t('categoryModals.newCategoryNamePlaceholder')}
          id="editCategoryNameField"
          useRef={forms}
          defaultValue={categorySelected?.label || ''}
          className="tw-text-base"
          onChange={(e) => {
            if (formRef) {
              formRef.current.categoryName = e.target.value;
            }
          }}
        />
        <p className="tw-text-xs tw-text-gray-500">
          {t('categoryModals.newCategoryNameHint')}
        </p>
      </div>

      {/* Areas assignment section */}
      <div className="tw-space-y-3">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Icon name="settings" color="blue" />
          <h4 className="tw-text-sm tw-font-medium tw-text-gray-700">
            {t('categoryModals.assignAdditionalAreas')}
          </h4>
        </div>
        <p className="tw-text-sm tw-text-gray-600">
          {t('categoryModals.assignAdditionalAreasHint')}
        </p>
        
        {/* Search input */}
        <Input
          icon="search"
          iconPosition="left"
          placeholder={t('categoryModals.searchAreas')}
          id="editFilterCategoryField"
          useRef={forms}
          fluid
        />

        {/* Areas list */}
        <div className="tw-max-h-48 tw-overflow-y-auto tw-border tw-border-gray-200 tw-rounded-lg">
          {(categorySelected?.isIncome ? incomeAreas : expenseAreas)
            ?.filter((area) => !categorySelected?.fields?.some(f => f.cashflowAreaId === area.cashflowAreaId))
            ?.filter((area) =>
              area.label.toLowerCase().includes((forms.current.editFilterCategoryField?.value || '').toLowerCase())
            )
            ?.map((area) => (
              <div 
                key={area.cashflowAreaId}
                className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-border-b tw-border-gray-100 hover:tw-bg-gray-50"
              >
                <input
                  type="checkbox"
                  id={`edit-area-${area.cashflowAreaId}`}
                  checked={formRef?.current?.areasToMove?.some(sa => sa.cashflowAreaId === area.cashflowAreaId)}
                  onChange={(e) => {
                    if (formRef) {
                      if (e.target.checked) {
                        formRef.current.areasToMove = [...(formRef.current.areasToMove || []), area];
                      } else {
                        formRef.current.areasToMove = (formRef.current.areasToMove || []).filter(sa => sa.cashflowAreaId !== area.cashflowAreaId);
                      }
                    }
                  }}
                  className="tw-w-4 tw-h-4 tw-text-blue-600 tw-border-gray-300 tw-rounded focus:tw-ring-blue-500"
                />
                <label 
                  htmlFor={`edit-area-${area.cashflowAreaId}`}
                  className="tw-text-sm tw-text-gray-700 tw-cursor-pointer tw-flex-1"
                >
                  {area.label}
                </label>
              </div>
            ))}
          {(categorySelected?.isIncome ? incomeAreas : expenseAreas)?.length === 0 && (
            <div className="tw-p-4 tw-text-center tw-text-gray-500">
              {t('categoryModals.noAreasAvailable')}
            </div>
          )}
        </div>
      </div>

      {/* Current areas info */}
      {categorySelected && categorySelected.fields && categorySelected.fields.length > 0 && (
        <div className="tw-bg-gray-50 tw-p-3 tw-rounded-lg">
          <p className="tw-text-sm tw-text-gray-700">
            <Icon name="info circle" color="blue" />
            {t('categoryModals.currentAreas', { count: categorySelected.fields.length })}
          </p>
        </div>
      )}

      {/* Selected areas count */}
      {formRef?.current?.areasToMove?.length > 0 && (
        <div className="tw-bg-blue-50 tw-p-3 tw-rounded-lg">
          <p className="tw-text-sm tw-text-blue-700">
            <Icon name="check circle" color="blue" />
            {t('categoryModals.additionalAreasSelected', { count: formRef.current.areasToMove.length })}
          </p>
        </div>
      )}
    </div>
  );
};

export default EditCategoryModal; 